// columns.js
import React from "react";
import ShowDataCell from "src/components/Common/DataCell";
import LabelStatus from "src/components/Common/LabelStatus";
import {
  ButtonCollapse,
  ButtonDownload,
  ButtonCopy,
  ButtonRetry,
  SingleDashboard,
  ButtonReferenceHistory,
  ButtonResultDisplay,
  ButtonAssignTagsToResources,
} from "src/components/Common/Button";
import CustomFilterButton from "src/components/Common/CustomFilterButton";
import { PROCESS_STATUS, SIMULATION_TYPE } from "src/constant/common";
import ProtectedComponent from "src/components/Permission";
import APP_ROUTES from "src/routes/routes";
import SeriesData from "src/components/GNCSimulator6DOF/components/SeriesData";
import useErrorDetails from "src/Hooks/useErrorDetails";
import useUserList from "src/Hooks/useUserList";

const LabelStatusWithErrorDetails = ({ row }) => {
  const errorList = useErrorDetails(row?.validate_error);
  return <LabelStatus row={row} errorList={errorList} />;
};

const DisplayUsername = ({ row }) => {
  const { data: usernameByIds } = useUserList();
  return usernameByIds?.[row?.updated_by] || row?.updated_by;
};

const columns = ({
  t,
  setDataModalReferenceHistory,
  getSimulators6DOFHistoryMutate,
  setCaseResult,
  handleClickDashboard,
  handleDownload,
  onClickCollapsed,
  seriesById,
  params,
  dashboardLoading,
  setDataModalFileView,
  allTagsData,
  handleFilterData,
  // multiResourcesSelected,
  handleOpenAssignTagsModal,
}) => [
  {
    dataField: "id",
    text: t("list.table.id"),
    // text: (
    //   <div className="d-flex align-items-center gap-1">
    //     <span className="text-nowrap">{t("list.table.id")}</span>
    //     <ButtonAssignTagsToResources
    //       isShow={multiResourcesSelected.length > 1}
    //       id="multi-resources"
    //       handleClick={handleOpenAssignTagsModal}
    //     />
    //   </div>
    // ),
    formatter: (id, row) => {
      return <ShowDataCell data={id} customClass="text-nowrwap" />;
    },
    headerClass: "width-by-percent-10 text-nowrwap",
  },
  {
    dataField: "tags",
    text: (
      <div className="d-flex align-items-center gap-1">
        {t("list.table.tags")}
        <CustomFilterButton
          name="tag_ids"
          className="width-by-px-400 left-30"
          options={allTagsData || []}
          handleApply={handleFilterData}
          defaultValue={params?.tag_ids || ""}
          isSearchable
          isMultiple
          closeMenuOnSelect={false}
        />
      </div>
    ),
    formatter: (tags, row) => (
      <ButtonAssignTagsToResources
        id={`resource-${row?.id}`}
        handleClick={() => handleOpenAssignTagsModal(row?.id)}
        tags={tags || []}
        tooltipText={tags?.map((tag) => tag?.name)?.join(", ") || "no_tags_found"}
      />
    ),
    headerClass: "width-by-percent-6 align-middle text-nowrap",
  },
  {
    dataField: "name",
    text: t("list.table.name"),
    headerClass: "width-by-percent-12 text-nowrwap",
    formatter: (name) => <ShowDataCell data={name} />,
  },
  {
    dataField: "status",
    text: t("list.table.status"),
    headerClass: "width-by-percent-13 text-nowrwap",
    formatter: (_, row) => <LabelStatusWithErrorDetails row={row} />,
  },
  {
    dataField: "param",
    text: t("list.table.param"),
    headerClass: "width-by-percent-7 text-nowrwap",
    formatter: (_, row, { isCollapse, toggleCollapse }) => (
      <ButtonCollapse handleCollapse={() => toggleCollapse(() => onClickCollapsed(row))} isCollapsed={!isCollapse} />
    ),
    collapseRow: (row) => {
      return (
        <SeriesData
          simulationData={row}
          isError={seriesById?.[row?.id]?.error}
          isLoading={seriesById?.[row?.id]?.loading}
          serieData={seriesById?.[row?.id]}
          handleViewFile={setDataModalFileView}
        />
      );
    },
  },
  {
    dataField: "type",
    text: t("list.table.mode"),
    headerClass: "width-by-percent-8 text-nowrwap",
    formatter: (_, row) => {
      return <>{row?.type === SIMULATION_TYPE.GNC_6DoF_MCS ? t("list.table.mcs_mode") : t("list.table.sim_mode")}</>;
    },
  },
  {
    dataField: "version_name",
    text: t("list.table.version"),
    headerClass: "width-by-percent-10 text-nowrwap",
  },
  {
    dataField: "updated_at",
    text: t("list.table.update_at"),
    headerClass: "width-by-percent-11 text-nowrwap",
  },
  {
    dataField: "updated_by",
    text: t("list.table.user"),
    headerClass: "width-by-percent-9 text-nowrwap",
    formatter: (_, row) => <DisplayUsername row={row} />,
  },
  {
    dataField: "note",
    text: t("list.table.note"),
    headerClass: "width-by-percent-9 text-nowrwap",
    formatter: (note) => <ShowDataCell data={note} />,
  },
  {
    dataField: "",
    text: "",
    headerClass: "width-by-px-55",
    formatter: (_, row) => (
      <ButtonResultDisplay
        id={row.id}
        isShow={row.status === PROCESS_STATUS.SUCCEEDED && row?.type === SIMULATION_TYPE.GNC_6DoF_MCS}
        handleClick={() => setCaseResult(row?.id)}
      />
    ),
  },
  {
    dataField: "",
    text: "",
    headerClass: "width-by-px-55",
    formatter: (_, row) => (
      <SingleDashboard
        id={row.id}
        isShow={row.status === PROCESS_STATUS.SUCCEEDED}
        isLoading={dashboardLoading?.[row?.id]}
        handleClick={() => handleClickDashboard(row)}
        isReady={!!row.simulator_dashboard?.dashboard_url}
      />
    ),
  },
  {
    dataField: "",
    text: "",
    headerClass: "width-by-px-55",
    formatter: (_, row) => <ButtonDownload handleDownload={() => handleDownload(row.id)} url={row.result_url} id={row.id} />,
  },
  {
    dataField: "",
    text: "",
    headerClass: "width-by-px-55",
    formatter: (_, row) => (
      <ButtonReferenceHistory
        handleClick={() => {
          getSimulators6DOFHistoryMutate.mutate(row.id, {
            onSuccess: (data) => {
              setDataModalReferenceHistory({ isOpen: true, simReferenceHistoryData: data?.items });
            },
          });
        }}
        id={row.id}
      />
    ),
  },
  {
    dataField: "",
    text: "",
    headerClass: "width-by-px-55",
    formatter: (_, row) => (
      <>
        <ProtectedComponent id={`gnc-copy-${row.id}`}>
          <ButtonCopy
            id={row.id}
            isShow={row.status === PROCESS_STATUS.SUCCEEDED}
            toUrl={`${APP_ROUTES.GNC_SIMULATOR_6DOF}/${row?.id}?action=copy${params.page ? "&page=" + params.page : ""}${
              params?.condition ? "&condition=" + params.condition : ""
            }${params?.tag_ids ? "&tag_ids=" + params.tag_ids : ""}`}
          />
        </ProtectedComponent>
        <ProtectedComponent id={`gnc-retry-${row.id}`} ownerId={row.created_by}>
          <ButtonRetry
            id={row.id}
            isShow={row.status === PROCESS_STATUS.FAILED}
            toUrl={`${APP_ROUTES.GNC_SIMULATOR_6DOF}/${row?.id}?action=retry${params.page ? "&page=" + params.page : ""}${
              params?.condition ? "&condition=" + params.condition : ""
            }${params?.tag_ids ? "&tag_ids=" + params.tag_ids : ""}`}
          />
        </ProtectedComponent>
      </>
    ),
  },
];

export default columns;
